import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import Input from "../../components/forms/Input";
import Select from "../../components/forms/Select";
import RichTextEditor from "../../components/editor/RichTextEditor";
import DocumentTree from "../../components/editor/DocumentTree";
import BaseModal from "../../components/modals/BaseModal";
import Avatar from "../../components/ui/Avatar";
import useAuth from "../../hooks/useAuth";
import useToast from "../../hooks/useToast";
import { meetingNoteService } from "../../api/meetingNoteService";
import useMeetingNoteSocket from "../../hooks/useMeetingNoteSocket";
import logger from "../../utils/logger";
import {
  ArrowLeft,
  Edit2,
  Plus,
  X,
  Settings,
  CheckCircle2,
  Clock,
  UserPlus,
  List,
  ChevronDown,
  Search,
  User,
  Eye,
  Share2,
  Link2,
  Copy,
  Check,
  Trash2,
} from "lucide-react";

const MeetingNoteEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { auth } = useAuth();
  const { showToast } = useToast();
  const currentUserId = auth?.employee?._id;

  const isNew = id === "new" || !id;
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get("token") || null;
  // Keep shareToken available inside callbacks via a stable ref
  const shareTokenRef = useRef(shareToken);
  shareTokenRef.current = shareToken;

  const [loading, setLoading] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [permissionSelectedUserId, setPermissionSelectedUserId] = useState("");
  const [permissionSelectedRole, setPermissionSelectedRole] =
    useState("editor"); // 'viewer' | 'editor'

  // Custom dropdown state
  const [
    isPermissionEmployeeDropdownOpen,
    setIsPermissionEmployeeDropdownOpen,
  ] = useState(false);
  const [isPermissionRoleDropdownOpen, setIsPermissionRoleDropdownOpen] =
    useState(false);
  const [permissionEmployeeSearchQuery, setPermissionEmployeeSearchQuery] =
    useState("");
  const permissionEmployeeDropdownRef = useRef(null);
  const permissionRoleDropdownRef = useRef(null);

  // Share link state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLinks, setShareLinks] = useState([]);
  const [loadingShareLinks, setLoadingShareLinks] = useState(false);
  const [newLinkPermission, setNewLinkPermission] = useState("view");
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  const [meetingNote, setMeetingNote] = useState({
    title: "",
    viewers: [],
    editors: [],
    tabs: [], // No default tabs - user creates them manually
  });

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isTreeOpen, setIsTreeOpen] = useState(false);
  const autoSaveTimeoutRef = useRef(null);
  const titleSaveTimeoutRef = useRef(null);
  const lastSavedContentRef = useRef({});
  const lastSavedTitleRef = useRef("");
  const meetingNoteRef = useRef(meetingNote);
  const isApplyingRemoteUpdateRef = useRef(false);
  const activeUsersRef = useRef([]);
  const saveCurrentTabBeforeSwitchRef = useRef(null);
  const activeTabIndexRef = useRef(0);
  const editorRef = useRef(null);

  const fetchMeetingNote = useCallback(async () => {
    try {
      setLoading(true);
      const response = await meetingNoteService.getMeetingNoteById(
        id,
        shareTokenRef.current,
      );
      const data = response.data?.data || response.data;

      if (!data.tabs || !Array.isArray(data.tabs)) {
        data.tabs = [{ name: "Notes", content: "", order: 0 }];
      } else if (data.tabs.length === 0) {
        data.tabs = [{ name: "Notes", content: "", order: 0 }];
      }
      if (!data.viewers) data.viewers = [];
      if (!data.editors) data.editors = [];

      if (data.tabs && Array.isArray(data.tabs)) {
        data.tabs.forEach((tab, index) => {
          lastSavedContentRef.current[index] = tab.content || "";
        });
      }

      setMeetingNote(data);
      setActiveTabIndex(0);
      lastSavedTitleRef.current = data.title || "";
    } catch (error) {
      logger.error("Failed to fetch meeting note:", error);
      showToast(
        error.response?.data?.message || "Failed to load meeting note",
        "error",
      );
      navigate("/communication/meeting-notes");
    } finally {
      setLoading(false);
    }
  }, [id, showToast, navigate]);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoadingEmployees(true);
      const response = await meetingNoteService.getEmployees();
      setEmployees(response.data?.data || response.data || []);
    } catch (error) {
      logger.error("Failed to fetch employees:", error);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  // Real-time collaboration socket (only for existing notes)
  // Use useMemo to prevent hook from being called on every render
  const socketNoteId = useMemo(() => (isNew ? null : id), [isNew, id]);
  const { isConnected, activeUsers, sendContentUpdate, onContentUpdate } =
    useMeetingNoteSocket(socketNoteId);

  // Keep refs in sync with state
  useEffect(() => {
    meetingNoteRef.current = meetingNote;
  }, [meetingNote]);

  useEffect(() => {
    activeUsersRef.current = activeUsers;
  }, [activeUsers]);

  useEffect(() => {
    activeTabIndexRef.current = activeTabIndex;
  }, [activeTabIndex]);

  useEffect(() => {
    if (isNew || activeUsers.length <= 1) return;

    const unsubscribe = onContentUpdate((payload) => {
      if (!payload?.tabs) return;
      if (isApplyingRemoteUpdateRef.current) return;

      isApplyingRemoteUpdateRef.current = true;

      setMeetingNote((prev) => ({
        ...prev,
        tabs: payload.tabs,
      }));

      payload.tabs.forEach((tab, index) => {
        lastSavedContentRef.current[index] = tab.content || "";
      });

      if (
        payload.activeTabIndex !== undefined &&
        payload.activeTabIndex === activeTabIndex
      ) {
        const updatedContent =
          payload.tabs[payload.activeTabIndex]?.content || "";
        lastSavedContentRef.current[payload.activeTabIndex] = updatedContent;
      }

      setTimeout(() => {
        isApplyingRemoteUpdateRef.current = false;
      }, 200);
    });

    return unsubscribe;
  }, [onContentUpdate, isNew, activeUsers.length, activeTabIndex]);

  useEffect(() => {
    if (!isNew && id) {
      fetchMeetingNote();
    } else {
      setLoading(false);
    }
    fetchEmployees();
  }, [id, isNew, fetchMeetingNote, fetchEmployees]);

  const tabsLength = meetingNote.tabs?.length ?? 0;
  useEffect(() => {
    if (meetingNote.tabs && Array.isArray(meetingNote.tabs)) {
      meetingNote.tabs.forEach((tab, index) => {
        if (lastSavedContentRef.current[index] === undefined) {
          lastSavedContentRef.current[index] = tab.content || "";
        }
      });
    }
    // Intentionally depend only on tabs length to avoid re-running on every content change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsLength]);

  // Cleanup: Save pending changes and clear timeout on unmount
  useEffect(() => {
    const lastSavedRef = lastSavedContentRef;
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }

      if (!isNew && saveCurrentTabBeforeSwitchRef.current) {
        const currentMeetingNote = meetingNoteRef.current;
        const currentActiveTabIndex = activeTabIndexRef.current;

        if (currentMeetingNote.tabs?.[currentActiveTabIndex]) {
          const currentContent =
            currentMeetingNote.tabs[currentActiveTabIndex]?.content || "";
          const lastSavedContent =
            lastSavedRef.current[currentActiveTabIndex] || "";

          if (
            currentContent !== lastSavedContent &&
            saveCurrentTabBeforeSwitchRef.current
          ) {
            saveCurrentTabBeforeSwitchRef
              .current(currentActiveTabIndex)
              .catch((error) => {
                logger.error("Failed to save on unmount:", error);
              });
          }
        }
      }
    };
  }, [isNew]);

  // Save current tab before switching - called when tab changes
  const saveCurrentTabBeforeSwitch = useCallback(
    async (tabIndexToSave) => {
      // Use ref to get latest state
      const currentMeetingNote = meetingNoteRef.current;
      if (
        isNew ||
        !currentMeetingNote.tabs ||
        !currentMeetingNote.tabs[tabIndexToSave]
      )
        return;

      const currentContent =
        currentMeetingNote.tabs[tabIndexToSave]?.content || "";
      const lastSavedContent =
        lastSavedContentRef.current[tabIndexToSave] || "";

      if (currentContent === lastSavedContent) return;

      try {
        await meetingNoteService.updateMeetingNote(
          id,
          {
            tabs: currentMeetingNote.tabs,
          },
          shareTokenRef.current,
        );

        lastSavedContentRef.current[tabIndexToSave] = currentContent;

        const activeUsersCount = activeUsersRef.current.length;
        if (sendContentUpdate && activeUsersCount > 1) {
          sendContentUpdate(currentMeetingNote.tabs, tabIndexToSave);
        }

        setHasUnsavedChanges(false);
      } catch (error) {
        logger.error("[MeetingNoteEditor] Auto-save failed:", error);
      }
    },
    [id, isNew, sendContentUpdate],
  );

  // Keep save function ref in sync
  useEffect(() => {
    saveCurrentTabBeforeSwitchRef.current = saveCurrentTabBeforeSwitch;
  }, [saveCurrentTabBeforeSwitch]);

  // Auto-save functionality with debounce - saves automatically after user stops typing for 2 seconds
  const activeTabContent = meetingNote.tabs?.[activeTabIndex]?.content;
  useEffect(() => {
    // Don't auto-save if:
    // - It's a new document
    // - No tabs exist
    // - Current tab doesn't exist
    // - This is a remote update (not user typing)
    if (
      isNew ||
      !meetingNote.tabs ||
      !meetingNote.tabs[activeTabIndex] ||
      isApplyingRemoteUpdateRef.current
    ) {
      return;
    }

    const currentContent = meetingNote.tabs[activeTabIndex]?.content || "";
    const lastSavedContent = lastSavedContentRef.current[activeTabIndex] || "";

    // Only proceed if content has actually changed
    if (currentContent === lastSavedContent) {
      return;
    }

    // Clear existing timeout to reset the debounce timer
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (2 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(async () => {
      // Use ref to get the latest save function
      if (saveCurrentTabBeforeSwitchRef.current) {
        await saveCurrentTabBeforeSwitchRef.current(activeTabIndex);
      }
    }, 2000);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [activeTabContent, activeTabIndex, isNew, meetingNote.tabs]);

  // Auto-save title when user stops typing (debounced)
  useEffect(() => {
    if (isNew || !id) return;
    const currentTitle = meetingNote.title?.trim() ?? "";
    const lastSaved = lastSavedTitleRef.current ?? "";
    if (currentTitle === lastSaved) return;

    if (titleSaveTimeoutRef.current) clearTimeout(titleSaveTimeoutRef.current);
    titleSaveTimeoutRef.current = setTimeout(async () => {
      try {
        await meetingNoteService.updateMeetingNote(id, {
          title: currentTitle || "Untitled",
        });
        lastSavedTitleRef.current = currentTitle || "Untitled";
      } catch (error) {
        logger.error("Failed to save title:", error);
      } finally {
        if (titleSaveTimeoutRef.current) {
          clearTimeout(titleSaveTimeoutRef.current);
          titleSaveTimeoutRef.current = null;
        }
      }
    }, 1500);

    return () => {
      if (titleSaveTimeoutRef.current)
        clearTimeout(titleSaveTimeoutRef.current);
    };
  }, [meetingNote.title, id, isNew]);

  // Removed handleSave - auto-save only, no manual save button

  const handleTabContentChange = (content) => {
    if (
      !meetingNote.tabs ||
      !meetingNote.tabs[activeTabIndex] ||
      isApplyingRemoteUpdateRef.current
    )
      return;

    const updatedTabs = [...meetingNote.tabs];
    updatedTabs[activeTabIndex] = {
      ...updatedTabs[activeTabIndex],
      content,
    };
    setMeetingNote({ ...meetingNote, tabs: updatedTabs });
    setHasUnsavedChanges(true);
    // Auto-save will be triggered by the useEffect hook
  };

  // Handle tab switch - save previous tab before switching
  const handleTabSwitch = async (newTabIndex) => {
    // Clear any pending auto-save timeout before switching tabs
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }

    // Save current tab before switching
    if (activeTabIndex !== newTabIndex && !isNew) {
      await saveCurrentTabBeforeSwitch(activeTabIndex);
    }
    setActiveTabIndex(newTabIndex);
    setHasUnsavedChanges(false);
    setIsTreeOpen(false); // Close tree when switching tabs
  };

  const handleAddTab = async () => {
    const currentTabs = meetingNote.tabs || [];
    // New tabs are named "Tab 2", "Tab 3", etc. (first tab is always "Notes")
    const tabName = `Tab ${currentTabs.length + 1}`;
    const newTab = {
      name: tabName,
      content: "",
      order: currentTabs.length,
    };
    const updatedTabs = [...currentTabs, newTab];
    const newTabIndex = updatedTabs.length - 1;

    setMeetingNote({
      ...meetingNote,
      tabs: updatedTabs,
    });
    // Automatically switch to the newly created tab
    setActiveTabIndex(newTabIndex);
    setHasUnsavedChanges(true);

    // Auto-save new tab creation
    if (!isNew) {
      try {
        await meetingNoteService.updateMeetingNote(
          id,
          {
            tabs: updatedTabs,
          },
          shareTokenRef.current,
        );
        setHasUnsavedChanges(false);
        // Broadcast via WebSocket only if there are multiple users
        if (sendContentUpdate && activeUsersRef.current.length > 1) {
          sendContentUpdate(updatedTabs, newTabIndex);
        }
      } catch (error) {
        logger.error("Failed to save new tab:", error);
      }
    }
  };

  const handleRenameTab = async (index, newName) => {
    if (!newName.trim() || !meetingNote.tabs || !meetingNote.tabs[index])
      return;
    const updatedTabs = [...meetingNote.tabs];
    updatedTabs[index] = {
      ...updatedTabs[index],
      name: newName.trim(),
    };
    setMeetingNote({ ...meetingNote, tabs: updatedTabs });
    setHasUnsavedChanges(true);

    // Auto-save tab rename
    if (!isNew) {
      try {
        await meetingNoteService.updateMeetingNote(
          id,
          {
            tabs: updatedTabs,
          },
          shareTokenRef.current,
        );
        setHasUnsavedChanges(false);
        // Broadcast via WebSocket only if there are multiple users
        if (sendContentUpdate && activeUsersRef.current.length > 1) {
          sendContentUpdate(updatedTabs, activeTabIndex);
        }
      } catch (error) {
        logger.error("Failed to save tab rename:", error);
      }
    }
  };

  const handleDeleteTab = async (index) => {
    if (!meetingNote.tabs || meetingNote.tabs.length <= 1) {
      showToast("Cannot delete the last tab", "error");
      return;
    }
    const updatedTabs = meetingNote.tabs.filter((_, i) => i !== index);
    const newActiveIndex =
      activeTabIndex >= updatedTabs.length
        ? updatedTabs.length - 1
        : activeTabIndex;

    setMeetingNote({ ...meetingNote, tabs: updatedTabs });
    setActiveTabIndex(newActiveIndex);
    setHasUnsavedChanges(true);

    // Auto-save tab deletion
    if (!isNew) {
      try {
        await meetingNoteService.updateMeetingNote(
          id,
          {
            tabs: updatedTabs,
          },
          shareTokenRef.current,
        );
        setHasUnsavedChanges(false);
        // Broadcast via WebSocket only if there are multiple users
        if (sendContentUpdate && activeUsersRef.current.length > 1) {
          sendContentUpdate(updatedTabs, newActiveIndex);
        }
      } catch (error) {
        logger.error("Failed to save tab deletion:", error);
      }
    }
  };

  // For new documents, user can always edit
  // For existing documents, check if user is creator or editor (direct access only)
  const hasDirectEditAccess =
    isNew ||
    (meetingNote.createdBy &&
      currentUserId &&
      (typeof meetingNote.createdBy === "object"
        ? meetingNote.createdBy._id?.toString() === currentUserId.toString()
        : meetingNote.createdBy.toString() === currentUserId.toString())) ||
    (meetingNote.editors &&
      currentUserId &&
      meetingNote.editors.some((e) => {
        const editorId =
          typeof e === "object" ? e._id?.toString() : e.toString();
        return editorId === currentUserId.toString();
      }));

  // Also grant edit access if this session is via a share token with edit permission
  const canEdit = hasDirectEditAccess || meetingNote.tokenPermission === "edit";

  const getEmployeeName = (employee) => {
    return employee?.fullNameEn || employee?.nickname || "Unknown";
  };

  const getEmployeeById = (employeeId) =>
    employees.find((emp) => emp._id === employeeId);

  const availableEmployeesForPermission = employees.filter(
    (emp) =>
      emp._id !== currentUserId &&
      !meetingNote.viewers?.some(
        (v) => (typeof v === "object" ? v._id : v) === emp._id,
      ) &&
      !meetingNote.editors?.some(
        (e) => (typeof e === "object" ? e._id : e) === emp._id,
      ),
  );

  // Filter employees for permission dropdown by search query
  const filteredPermissionEmployees = availableEmployeesForPermission.filter(
    (emp) => {
      const query = permissionEmployeeSearchQuery.toLowerCase();
      return (
        getEmployeeName(emp).toLowerCase().includes(query) ||
        emp.employeeCode?.toLowerCase().includes(query)
      );
    },
  );

  // Permission role options
  const permissionRoleOptions = [
    { value: "viewer", label: "Viewer", icon: Eye },
    { value: "editor", label: "Editor", icon: Edit2 },
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        permissionEmployeeDropdownRef.current &&
        !permissionEmployeeDropdownRef.current.contains(e.target)
      ) {
        setIsPermissionEmployeeDropdownOpen(false);
        setPermissionEmployeeSearchQuery("");
      }
      if (
        permissionRoleDropdownRef.current &&
        !permissionRoleDropdownRef.current.contains(e.target)
      ) {
        setIsPermissionRoleDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAddPermission = () => {
    if (!permissionSelectedUserId) return;
    const id = permissionSelectedUserId;
    if (permissionSelectedRole === "editor") {
      setMeetingNote({
        ...meetingNote,
        editors: [...(meetingNote.editors || []), id],
      });
    } else {
      setMeetingNote({
        ...meetingNote,
        viewers: [...(meetingNote.viewers || []), id],
      });
    }
    setPermissionSelectedUserId("");
    setPermissionSelectedRole("editor");
  };

  const handleRemovePermission = (employeeId) => {
    const idStr = (x) => (typeof x === "object" ? x?._id : x)?.toString();
    const empStr = employeeId?.toString();
    setMeetingNote({
      ...meetingNote,
      viewers: (meetingNote.viewers || []).filter((v) => idStr(v) !== empStr),
      editors: (meetingNote.editors || []).filter((e) => idStr(e) !== empStr),
    });
  };

  const handleChangePermissionRole = (employeeId, newRole) => {
    const idStr = (x) => (typeof x === "object" ? x?._id : x)?.toString();
    const empStr = employeeId?.toString();
    const newViewers = (meetingNote.viewers || []).filter(
      (v) => idStr(v) !== empStr,
    );
    const newEditors = (meetingNote.editors || []).filter(
      (e) => idStr(e) !== empStr,
    );
    if (newRole === "editor") {
      newEditors.push(employeeId);
    } else {
      newViewers.push(employeeId);
    }
    setMeetingNote({
      ...meetingNote,
      viewers: newViewers,
      editors: newEditors,
    });
  };

  const getPermissionList = () => {
    const list = [];
    (meetingNote.viewers || []).forEach((v) => {
      const id = typeof v === "object" ? v._id : v;
      list.push({ employeeId: id, role: "viewer" });
    });
    (meetingNote.editors || []).forEach((e) => {
      const id = typeof e === "object" ? e._id : e;
      list.push({ employeeId: id, role: "editor" });
    });
    return list;
  };

  const handleSavePermissions = async () => {
    try {
      setSavingPermissions(true);
      await meetingNoteService.updateMeetingNote(id, {
        viewers: meetingNote.viewers,
        editors: meetingNote.editors,
      });
      showToast("Permissions updated", "success");
      setShowPermissions(false);
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to update permissions",
        "error",
      );
    } finally {
      setSavingPermissions(false);
    }
  };

  // ─── Share Link Handlers ─────────────────────────────────────────────────

  const fetchShareLinks = useCallback(async () => {
    if (!id || isNew) return;
    try {
      setLoadingShareLinks(true);
      const response = await meetingNoteService.getShareLinks(id);
      setShareLinks(response.data?.data || response.data || []);
    } catch (error) {
      logger.error("Failed to fetch share links:", error);
    } finally {
      setLoadingShareLinks(false);
    }
  }, [id, isNew]);

  const handleGenerateShareLink = async () => {
    try {
      setGeneratingLink(true);
      const response = await meetingNoteService.generateShareLink(
        id,
        newLinkPermission,
      );
      const newLink = response.data?.data || response.data;
      setShareLinks((prev) => [...prev, newLink]);
      showToast("Share link generated", "success");
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to generate link",
        "error",
      );
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleRevokeShareLink = async (token) => {
    try {
      await meetingNoteService.revokeShareLink(id, token);
      setShareLinks((prev) => prev.filter((l) => l.token !== token));
      showToast("Link revoked", "success");
    } catch (error) {
      showToast("Failed to revoke link", "error");
    }
  };

  const handleUpdateShareLinkPermission = async (token, permission) => {
    try {
      await meetingNoteService.updateShareLinkPermission(id, token, permission);
      setShareLinks((prev) =>
        prev.map((l) => (l.token === token ? { ...l, permission } : l)),
      );
    } catch (error) {
      showToast("Failed to update permission", "error");
    }
  };

  const handleCopyShareLink = (token) => {
    const url = `${window.location.origin}/communication/meeting-notes/${id}?token=${token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    });
  };

  // Loading Skeleton Component
  const NoteEditorSkeleton = () => (
    <PageWrapper>
      <div className="h-screen flex flex-col overflow-hidden">
        {/* Header Skeleton - Fixed */}
        <div className="flex items-center justify-between shrink-0 pb-6">
          <div className="flex items-center gap-4">
            <div className="h-10 w-20 bg-gray-200 dark:bg-dark-200 rounded-lg animate-pulse"></div>
            <div className="h-8 bg-gray-200 dark:bg-dark-200 rounded w-64 animate-pulse"></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-10 w-32 bg-gray-200 dark:bg-dark-200 rounded-lg animate-pulse"></div>
            <div className="h-10 w-24 bg-gray-200 dark:bg-dark-200 rounded-lg animate-pulse"></div>
          </div>
        </div>

        {/* Note workspace skeleton – vertical tabs + content - Takes remaining height */}
        <div className="flex flex-col md:flex-row gap-0 bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 overflow-hidden shadow-sm flex-1 min-h-0">
          <div className="flex flex-col gap-2 p-3 border-b md:border-b-0 md:border-r border-gray-200 dark:border-dark-100 md:min-w-[180px] md:w-[180px] shrink-0">
            <div className="h-10 w-full bg-gray-200 dark:bg-dark-200 rounded-lg animate-pulse"></div>
            <div className="h-10 w-full bg-gray-200 dark:bg-dark-200 rounded-lg animate-pulse"></div>
            <div className="h-10 w-full bg-gray-200 dark:bg-dark-200 rounded-lg animate-pulse"></div>
          </div>
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="space-y-4">
                {/* Toolbar Skeleton */}
                <div className="flex items-center gap-2 pb-4 border-b border-gray-200 dark:border-dark-100">
                  <div className="h-8 w-8 bg-gray-200 dark:bg-dark-200 rounded animate-pulse"></div>
                  <div className="h-8 w-8 bg-gray-200 dark:bg-dark-200 rounded animate-pulse"></div>
                  <div className="h-8 w-8 bg-gray-200 dark:bg-dark-200 rounded animate-pulse"></div>
                  <div className="h-8 w-8 bg-gray-200 dark:bg-dark-200 rounded animate-pulse"></div>
                  <div className="h-8 w-px bg-gray-300 dark:bg-dark-300 mx-2"></div>
                  <div className="h-8 w-8 bg-gray-200 dark:bg-dark-200 rounded animate-pulse"></div>
                  <div className="h-8 w-8 bg-gray-200 dark:bg-dark-200 rounded animate-pulse"></div>
                  <div className="h-8 w-8 bg-gray-200 dark:bg-dark-200 rounded animate-pulse"></div>
                  <div className="h-8 w-px bg-gray-300 dark:bg-dark-300 mx-2"></div>
                  <div className="h-8 w-8 bg-gray-200 dark:bg-dark-200 rounded animate-pulse"></div>
                  <div className="h-8 w-8 bg-gray-200 dark:bg-dark-200 rounded animate-pulse"></div>
                </div>

                {/* Editor Content Skeleton */}
                <div className="space-y-3 pt-4">
                  <div className="h-6 bg-gray-200 dark:bg-dark-200 rounded w-full animate-pulse"></div>
                  <div className="h-6 bg-gray-200 dark:bg-dark-200 rounded w-5/6 animate-pulse"></div>
                  <div className="h-6 bg-gray-200 dark:bg-dark-200 rounded w-full animate-pulse"></div>
                  <div className="h-6 bg-gray-200 dark:bg-dark-200 rounded w-4/5 animate-pulse"></div>
                  <div className="h-6 bg-gray-200 dark:bg-dark-200 rounded w-full animate-pulse"></div>
                  <div className="h-6 bg-gray-200 dark:bg-dark-200 rounded w-3/4 animate-pulse"></div>
                  <div className="h-6 bg-gray-200 dark:bg-dark-200 rounded w-5/6 animate-pulse"></div>
                  <div className="h-6 bg-gray-200 dark:bg-dark-200 rounded w-full animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );

  // Only show loading skeleton for existing documents being fetched
  if (loading && !isNew) {
    return <NoteEditorSkeleton />;
  }

  return (
    <PageWrapper>
      <div className="h-screen flex flex-col overflow-hidden">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between shrink-0 pb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/communication/meeting-notes")}
              icon={<ArrowLeft className="w-4 h-4" />}
            >
              Back
            </Button>
            {isNew ? (
              <h1 className="text-2xl font-bold text-light-100 dark:text-light-100">
                Note Editor
              </h1>
            ) : (
              <div className="flex items-center gap-3">
                {canEdit ? (
                  <Input
                    value={meetingNote.title}
                    onChange={(e) =>
                      setMeetingNote({ ...meetingNote, title: e.target.value })
                    }
                    className="text-2xl font-bold"
                    placeholder="Meeting title..."
                  />
                ) : (
                  <h1 className="text-2xl font-bold text-light-100 dark:text-light-100">
                    {meetingNote.title}
                  </h1>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasDirectEditAccess && !isNew && (
              <Button
                variant="outline"
                onClick={() => {
                  setShowShareModal(true);
                  fetchShareLinks();
                }}
                icon={<Share2 className="w-4 h-4" />}
              >
                Share
              </Button>
            )}
            {hasDirectEditAccess && !isNew && (
              <Button
                variant="outline"
                onClick={() => setShowPermissions(!showPermissions)}
                icon={<Settings className="w-4 h-4" />}
                className={
                  showPermissions
                    ? "border-primary-accent bg-primary-accent/10 dark:bg-primary-accent/20 text-primary-accent"
                    : ""
                }
              >
                Permissions
              </Button>
            )}
            {hasUnsavedChanges && (
              <div className="flex items-center gap-2 text-sm text-primary-accent">
                <Clock className="w-4 h-4 animate-pulse" />
                <span>Saving...</span>
              </div>
            )}
            {!hasUnsavedChanges && !isNew && (
              <div className="flex items-center gap-2 text-sm text-primary-accent">
                <CheckCircle2 className="w-4 h-4" />
                <span>Saved</span>
              </div>
            )}
          </div>
        </div>

        {/* Permissions Modal Popup */}
        <BaseModal
          isOpen={showPermissions && hasDirectEditAccess}
          onClose={() => setShowPermissions(false)}
          title="Access Permissions"
          subtitle="Choose who can access this note and whether they can view or edit"
          size="lg"
          portal
          footer={
            <>
              <Button
                variant="outline"
                onClick={() => setShowPermissions(false)}
                disabled={savingPermissions}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePermissions}
                disabled={savingPermissions}
              >
                {savingPermissions ? "Saving..." : "Save Permissions"}
              </Button>
            </>
          }
        >
          <div className="space-y-5">
            {/* Add user: choose who + what role */}
            <div className="bg-gray-50 dark:bg-dark-200 p-4 rounded-xl border border-gray-100 dark:border-dark-300">
              <p className="text-sm font-medium text-light-100 dark:text-light-100 mb-3">
                Add person
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Employee Dropdown */}
                <div className="flex-1">
                  {loadingEmployees ? (
                    <div className="h-11 bg-gray-200 dark:bg-dark-200 rounded-lg animate-pulse"></div>
                  ) : (
                    <div
                      className="relative"
                      ref={permissionEmployeeDropdownRef}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setIsPermissionEmployeeDropdownOpen(
                            !isPermissionEmployeeDropdownOpen,
                          );
                          setPermissionEmployeeSearchQuery("");
                        }}
                        disabled={availableEmployeesForPermission.length === 0}
                        className="w-full flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 rounded-lg text-left text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all cursor-pointer hover:border-primary-accent disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <User className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="flex-1 truncate">
                          {permissionSelectedUserId
                            ? (() => {
                                const emp = employees.find(
                                  (e) => e._id === permissionSelectedUserId,
                                );
                                return emp
                                  ? getEmployeeName(emp)
                                  : "Select employee...";
                              })()
                            : "Select employee..."}
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${
                            isPermissionEmployeeDropdownOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      {isPermissionEmployeeDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-primary-card border border-gray-300 dark:border-dark-200 rounded-lg shadow-lg overflow-hidden">
                          <div className="sticky top-0 p-2 bg-white dark:bg-primary-card border-b border-gray-300 dark:border-dark-200">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type="text"
                                value={permissionEmployeeSearchQuery}
                                onChange={(e) =>
                                  setPermissionEmployeeSearchQuery(
                                    e.target.value,
                                  )
                                }
                                placeholder="Search employees..."
                                className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-300 dark:border-dark-200 bg-white dark:bg-dark-100 text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div className="max-h-52 overflow-y-auto">
                            {filteredPermissionEmployees.length === 0 ? (
                              <div className="px-4 py-3 text-sm text-gray-500 dark:text-light-500 text-center">
                                {permissionEmployeeSearchQuery.trim()
                                  ? "No employees found"
                                  : "No employees available"}
                              </div>
                            ) : (
                              filteredPermissionEmployees.map((emp) => {
                                const isSelected =
                                  emp._id === permissionSelectedUserId;
                                return (
                                  <button
                                    key={emp._id}
                                    type="button"
                                    onClick={() => {
                                      setPermissionSelectedUserId(emp._id);
                                      setIsPermissionEmployeeDropdownOpen(
                                        false,
                                      );
                                      setPermissionEmployeeSearchQuery("");
                                    }}
                                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-200 transition-colors ${
                                      isSelected
                                        ? "bg-primary-accent/10 text-primary-accent font-medium"
                                        : ""
                                    }`}
                                  >
                                    <div className="text-sm font-medium text-gray-900 dark:text-light-100">
                                      {getEmployeeName(emp)}
                                    </div>
                                    {emp.employeeCode && (
                                      <div className="text-xs text-gray-500 dark:text-light-500">
                                        {emp.employeeCode}
                                      </div>
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Role Dropdown */}
                <div className="w-full sm:w-40">
                  <div className="relative" ref={permissionRoleDropdownRef}>
                    <button
                      type="button"
                      onClick={() =>
                        setIsPermissionRoleDropdownOpen(
                          !isPermissionRoleDropdownOpen,
                        )
                      }
                      className="w-full flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 rounded-lg text-left text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all cursor-pointer hover:border-primary-accent"
                    >
                      {(() => {
                        const option = permissionRoleOptions.find(
                          (o) => o.value === permissionSelectedRole,
                        );
                        const Icon = option?.icon;
                        return (
                          <>
                            {Icon && (
                              <Icon className="w-4 h-4 text-gray-400 shrink-0" />
                            )}
                            <span className="flex-1 truncate">
                              {option?.label}
                            </span>
                          </>
                        );
                      })()}
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${
                          isPermissionRoleDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isPermissionRoleDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-primary-card border border-gray-300 dark:border-dark-200 rounded-lg shadow-lg overflow-hidden">
                        <div className="py-1">
                          {permissionRoleOptions.map((option) => {
                            const isSelected =
                              option.value === permissionSelectedRole;
                            const Icon = option.icon;
                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                  setPermissionSelectedRole(option.value);
                                  setIsPermissionRoleDropdownOpen(false);
                                }}
                                className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-200 transition-colors flex items-center gap-2 ${
                                  isSelected
                                    ? "bg-primary-accent/10 text-primary-accent font-medium"
                                    : ""
                                }`}
                              >
                                <Icon className="w-4 h-4 shrink-0" />
                                <span className="text-sm font-medium text-gray-900 dark:text-light-100">
                                  {option.label}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-end">
                  <Button
                    onClick={handleAddPermission}
                    disabled={!permissionSelectedUserId || loadingEmployees}
                    icon={<Plus className="w-4 h-4" />}
                    className="w-full sm:w-auto"
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>

            {/* Added users list */}
            {getPermissionList().length > 0 ? (
              <div>
                <p className="text-sm font-medium text-light-100 dark:text-light-100 mb-3">
                  People with access ({getPermissionList().length})
                </p>
                <div className="space-y-2 max-h-[240px] overflow-y-auto scrollbar-thin">
                  {getPermissionList().map(({ employeeId, role }) => {
                    const employee = getEmployeeById(employeeId);
                    if (!employee) return null;
                    return (
                      <motion.div
                        key={employeeId}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center justify-between p-3 bg-white dark:bg-dark-100 rounded-xl border border-gray-200 dark:border-dark-300"
                      >
                        <span className="font-medium text-light-100 dark:text-light-100">
                          {getEmployeeName(employee)}
                        </span>
                        <div className="flex items-center gap-2">
                          <Select
                            label=""
                            value={role}
                            onChange={(e) =>
                              handleChangePermissionRole(
                                employeeId,
                                e.target.value,
                              )
                            }
                            placeholder="Role"
                            options={[
                              { value: "viewer", label: "Viewer" },
                              { value: "editor", label: "Editor" },
                            ]}
                            className="w-28"
                          />
                          <button
                            onClick={() => handleRemovePermission(employeeId)}
                            className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Remove"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-10 text-light-500 dark:text-light-500 border border-dashed border-gray-300 dark:border-dark-300 rounded-xl">
                <UserPlus className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No one has access yet</p>
                <p className="text-xs mt-1">
                  Add people above to grant them access
                </p>
              </div>
            )}
          </div>
        </BaseModal>

        {/* Share Link Modal */}
        <BaseModal
          isOpen={showShareModal && hasDirectEditAccess}
          onClose={() => setShowShareModal(false)}
          title="Share Note"
          subtitle="Generate a shareable link — anyone with the link can access this note"
          size="lg"
          portal
          footer={
            <Button variant="outline" onClick={() => setShowShareModal(false)}>
              Close
            </Button>
          }
        >
          <div className="space-y-6">
            {/* Generate new link section */}
            <div className="bg-gray-50 dark:bg-dark-200 p-4 rounded-xl border border-gray-100 dark:border-dark-300">
              <p className="text-sm font-medium text-light-100 dark:text-light-100 mb-3">
                Generate new link
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Permission picker */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setNewLinkPermission("view")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                      newLinkPermission === "view"
                        ? "border-primary-accent bg-primary-accent/10 text-primary-accent dark:text-orange-400"
                        : "border-gray-300 dark:border-dark-200 text-gray-600 dark:text-light-400 hover:border-primary-accent"
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    View Only
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewLinkPermission("edit")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                      newLinkPermission === "edit"
                        ? "border-primary-accent bg-primary-accent/10 text-primary-accent dark:text-orange-400"
                        : "border-gray-300 dark:border-dark-200 text-gray-600 dark:text-light-400 hover:border-primary-accent"
                    }`}
                  >
                    <Edit2 className="w-4 h-4" />
                    Can Edit
                  </button>
                </div>
                <Button
                  onClick={handleGenerateShareLink}
                  disabled={generatingLink}
                  icon={<Link2 className="w-4 h-4" />}
                  className="sm:ml-auto"
                >
                  {generatingLink ? "Generating..." : "Generate Link"}
                </Button>
              </div>
            </div>

            {/* Active links */}
            <div>
              <p className="text-sm font-medium text-light-100 dark:text-light-100 mb-3">
                Active links
                {shareLinks.length > 0 && (
                  <span className="ml-2 text-xs font-normal text-light-500 dark:text-light-500">
                    ({shareLinks.length})
                  </span>
                )}
              </p>

              {loadingShareLinks ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => (
                    <div
                      key={i}
                      className="h-14 bg-gray-100 dark:bg-dark-200 rounded-xl animate-pulse"
                    />
                  ))}
                </div>
              ) : shareLinks.length === 0 ? (
                <div className="text-center py-8 text-light-500 dark:text-light-500 border border-dashed border-gray-300 dark:border-dark-300 rounded-xl">
                  <Link2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No active share links yet</p>
                  <p className="text-xs mt-1 opacity-70">
                    Generate one above to share this note
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {shareLinks.map((link) => {
                    const shareUrl = `${window.location.origin}/communication/meeting-notes/${id}?token=${link.token}`;
                    const isCopied = copiedToken === link.token;
                    return (
                      <div
                        key={link.token}
                        className="flex items-center gap-3 p-3 bg-white dark:bg-dark-100 rounded-xl border border-gray-200 dark:border-dark-100"
                      >
                        {/* Permission badge */}
                        <span
                          className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                            link.permission === "edit"
                              ? "bg-primary-accent/15 text-primary-accent dark:text-orange-400"
                              : "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                          }`}
                        >
                          {link.permission === "edit" ? (
                            <Edit2 className="w-3 h-3" />
                          ) : (
                            <Eye className="w-3 h-3" />
                          )}
                          {link.permission === "edit"
                            ? "Can Edit"
                            : "View Only"}
                        </span>

                        {/* Truncated URL */}
                        <span className="flex-1 text-xs text-gray-400 dark:text-light-500 truncate font-mono">
                          {shareUrl.replace(/^https?:\/\//, "")}
                        </span>

                        {/* Toggle permission */}
                        <button
                          type="button"
                          onClick={() =>
                            handleUpdateShareLinkPermission(
                              link.token,
                              link.permission === "view" ? "edit" : "view",
                            )
                          }
                          title="Toggle permission"
                          className="p-1.5 text-gray-400 hover:text-primary-accent hover:bg-primary-accent/10 rounded-lg transition-colors shrink-0"
                        >
                          {link.permission === "view" ? (
                            <Edit2 className="w-3.5 h-3.5" />
                          ) : (
                            <Eye className="w-3.5 h-3.5" />
                          )}
                        </button>

                        {/* Copy button */}
                        <button
                          type="button"
                          onClick={() => handleCopyShareLink(link.token)}
                          title="Copy link"
                          className="p-1.5 text-gray-400 hover:text-primary-accent hover:bg-primary-accent/10 rounded-lg transition-colors shrink-0"
                        >
                          {isCopied ? (
                            <Check className="w-3.5 h-3.5 text-green-500" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>

                        {/* Revoke button */}
                        <button
                          type="button"
                          onClick={() => handleRevokeShareLink(link.token)}
                          title="Revoke link"
                          className="p-1.5 text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors shrink-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </BaseModal>

        {/* Note workspace: vertical tabs (left) + content (right) - Takes remaining height */}
        <div className="flex flex-col md:flex-row gap-0 bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 overflow-hidden shadow-sm flex-1 min-h-0">
          {/* Vertical tabs – left sidebar - Fixed */}
          <div className="flex flex-col gap-1 p-3 border-b md:border-b-0 md:border-r border-gray-200 dark:border-dark-100 md:min-w-[180px] md:w-[180px] shrink-0 overflow-y-auto scrollbar-thin">
            {(meetingNote.tabs || []).map((tab, index) => (
              <VerticalTabButton
                key={index}
                tab={tab}
                isActive={index === activeTabIndex}
                onClick={() => {
                  if (index === activeTabIndex) {
                    // If clicking the active tab, toggle tree
                    setIsTreeOpen(!isTreeOpen);
                  } else {
                    // If clicking a different tab, switch to it
                    handleTabSwitch(index);
                  }
                }}
                onShowTree={() => setIsTreeOpen(!isTreeOpen)}
                onRename={(newName) => handleRenameTab(index, newName)}
                onDelete={() => handleDeleteTab(index)}
                canEdit={canEdit}
              />
            ))}
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAddTab}
                icon={<Plus className="w-4 h-4" />}
                className="shrink-0 justify-start w-full md:w-auto"
              >
                Add Tab
              </Button>
            )}
          </div>

          {/* Tab content – main area - Scrollable */}
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            {/* Text container header – active users (icons only, name on hover) - Fixed */}
            {!isNew && (
              <div className="flex items-center justify-end gap-1 px-4 pt-3 pb-1 border-b border-gray-200 dark:border-dark-100 shrink-0">
                {isConnected && activeUsers.length > 0 ? (
                  <>
                    {activeUsers.slice(0, 6).map((user, idx) => {
                      const name = getEmployeeName(user);
                      const isYou =
                        user._id?.toString() === currentUserId?.toString();
                      return (
                        <div
                          key={user._id}
                          className="relative group"
                          style={{ marginLeft: idx > 0 ? "-6px" : 0 }}
                          title={isYou ? `${name} (you)` : name}
                        >
                          <div
                            className={`ring-2 rounded-full ring-white dark:ring-primary-card transition-transform group-hover:z-10 group-hover:scale-110 ${
                              isYou ? "ring-primary-accent" : ""
                            }`}
                          >
                            <Avatar src={user.photo} alt={name} size="sm" />
                          </div>
                          <div className="absolute right-0 top-full mt-1 px-2 py-1 rounded bg-gray-800 dark:bg-dark-300 text-white dark:text-light-100 text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-20 shadow-lg">
                            {name}
                            {isYou && " (you)"}
                          </div>
                        </div>
                      );
                    })}
                    {activeUsers.length > 6 && (
                      <div
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-dark-100 dark:bg-dark-200 text-light-500 text-xs font-semibold ring-2 ring-white dark:ring-primary-card -ml-1"
                        title={`${activeUsers.length - 6} more`}
                      >
                        +{activeUsers.length - 6}
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}
            {/* Editor area with tree - Editor handles its own scrolling */}
            <div className="flex-1 min-w-0 flex flex-row min-h-0">
              <div className="flex-1 p-4 md:p-6 min-h-0 flex flex-col">
                {meetingNote.tabs &&
                meetingNote.tabs.length > 0 &&
                meetingNote.tabs[activeTabIndex] ? (
                  <RichTextEditor
                    ref={editorRef}
                    key={`tab-${activeTabIndex}`}
                    value={meetingNote.tabs[activeTabIndex]?.content || ""}
                    onChange={handleTabContentChange}
                    placeholder={`Start writing in ${meetingNote.tabs[activeTabIndex]?.name || "this tab"}...`}
                    readOnly={!canEdit}
                    tabs={meetingNote.tabs || []}
                    activeTabIndex={activeTabIndex}
                    onTabMentionClick={handleTabSwitch}
                  />
                ) : (
                  <div className="text-center py-12 text-light-500 dark:text-light-500">
                    <p>No tabs available</p>
                  </div>
                )}
              </div>

              {/* Document Tree Panel */}
              {meetingNote.tabs &&
                meetingNote.tabs.length > 0 &&
                meetingNote.tabs[activeTabIndex] && (
                  <DocumentTree
                    content={meetingNote.tabs[activeTabIndex]?.content || ""}
                    isOpen={isTreeOpen}
                    onClose={() => setIsTreeOpen(false)}
                    editorRef={editorRef}
                  />
                )}
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

// Vertical tab button – for left sidebar
const VerticalTabButton = ({
  tab,
  isActive,
  onClick,
  onShowTree,
  onRename,
  onDelete,
  canEdit,
}) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState(tab.name);
  const inputRef = React.useRef(null);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleRename = () => {
    if (newName.trim() && newName !== tab.name) {
      onRename(newName);
    }
    setIsRenaming(false);
    setNewName(tab.name);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleRename();
    } else if (e.key === "Escape") {
      setIsRenaming(false);
      setNewName(tab.name);
    }
  };

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors shrink-0 w-full border-l-4 ${
        isActive
          ? "bg-primary-accent/10 dark:bg-primary-accent/20 border-primary-accent text-primary-accent dark:text-primary-accent"
          : "border-transparent bg-gray-50 dark:bg-dark-200 hover:bg-gray-100 dark:hover:bg-dark-300 text-light-500 dark:text-light-500"
      }`}
      onClick={!isRenaming ? onClick : undefined}
      onDoubleClick={
        !isRenaming && canEdit ? () => setIsRenaming(true) : undefined
      }
    >
      {isRenaming ? (
        <input
          ref={inputRef}
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onBlur={handleRename}
          onKeyDown={handleKeyDown}
          className="flex-1 min-w-0 text-sm font-medium bg-transparent border-b border-primary-accent focus:outline-none text-light-100 dark:text-light-100"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <>
          <span
            className={`flex-1 min-w-0 truncate text-sm font-medium ${
              isActive ? "text-primary-accent" : ""
            }`}
          >
            {tab.name}
          </span>
          {isActive && (
            <div className="flex items-center gap-0.5 shrink-0">
              {onShowTree && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowTree();
                  }}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-dark-300 rounded text-light-500 dark:text-light-500 hover:text-light-100 dark:hover:text-light-100"
                  title="Show document tree"
                >
                  <List className="w-3 h-3" />
                </button>
              )}
              {canEdit && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsRenaming(true);
                    }}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-dark-300 rounded text-light-500 dark:text-light-500 hover:text-light-100 dark:hover:text-light-100"
                    title="Rename tab"
                  >
                    <Edit2 className="w-3 h-3" />
                  </button>
                  {onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                      }}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded text-red-500"
                      title="Delete tab"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MeetingNoteEditor;
